import { FC, HTMLAttributes, MouseEvent } from "react";
import { ArrowRight, ExternalLink } from "react-feather";
import clsx from "clsx";
import TButton from "./TButton";
import { TButtonProps } from "./TButton";
import { trackEvent } from "@/utils/analytics";

// -----------------------------------------------------------------------

export type CTAButtonProps = Omit<TButtonProps, "size"> & {
  showIcon?: boolean;
  iconPosition?: "left" | "right";
  iconType?: "arrow" | "external";
  size?: "sm" | "md" | "lg" | "large";
};

const CTAButton: FC<CTAButtonProps> = ({
  children,
  showIcon = true,
  iconPosition = "right",
  iconType = "arrow",
  size = "md",
  className,
  variant = "white",
  radius = "full",
  ...props
}) => {
  // Responsive size classes — mobile-first. Each tier scales padding +
  // font-size up at md and lg breakpoints so the button reads at the
  // right scale on phones (where the previous fixed `px-12 py-4 text-h7`
  // for size="large" made the hero CTA absurdly big and would push the
  // text off-screen) AND on desktops.
  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "px-4 py-1.5 text-sm md:px-5 md:py-2 md:text-base";
      case "lg":
        return "px-6 py-2.5 text-base md:px-8 md:py-3 md:text-lg lg:px-10 lg:text-h7";
      case "large":
        return "px-6 py-3 text-base md:px-8 md:py-3.5 md:text-lg lg:px-12 lg:py-4 lg:text-h7";
      default: // md
        return "px-5 py-2 text-sm md:px-7 md:text-base lg:px-8 lg:text-lg";
    }
  };

  // Icon size — picked at runtime from viewport width so it scales with
  // the button. SSR returns the smallest tier to avoid hydration
  // mismatch; the useState swap below refines after mount.
  const getIconSize = () => {
    switch (size) {
      case "sm":
        return 14;
      case "lg":
        return 20;
      case "large":
        return 22;
      default: // md
        return 18;
    }
  };

  const renderIcon = () => {
    if (!showIcon) return null;

    const iconSize = getIconSize();
    const iconClass =
      "transition-transform duration-300 group-hover:translate-x-1";

    if (iconType === "external") {
      return <ExternalLink size={iconSize} className={iconClass} />;
    }

    return <ArrowRight size={iconSize} className={iconClass} />;
  };

  // Map CTAButton sizes to TButton sizes
  const getTButtonSize = () => {
    switch (size) {
      case "sm":
        return "base";
      case "lg":
        return "large";
      case "large":
        return "large";
      default:
        return "base";
    }
  };

  // Auto-track every CTAButton click. The cta_id is required to land in
  // GA4 cleanly — every CTA in the codebase already carries an `id` (e.g.
  // `get-consultancy-desktop-header-button`) so this gives us a complete
  // funnel without per-call instrumentation. Falls back gracefully when
  // `id` is missing.
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    const ctaText =
      typeof children === "string" ? children : (props.id ?? "unknown");
    // Special-case the security-assessment CTA so it lands as the more
    // useful `start_assessment` conversion in GA4 (instead of a generic
    // cta_click that's harder to mark as a funnel step).
    const isAssessment =
      props.id?.includes("assessment") ||
      props.href?.includes("assessment") ||
      props.href?.includes("security-assessment");
    trackEvent(isAssessment ? "start_assessment" : "cta_click", {
      cta_id: props.id || "unnamed",
      cta_text: String(ctaText).slice(0, 80),
      cta_variant: variant,
      cta_href: props.href,
    });
    (props as { onClick?: (e: MouseEvent<HTMLButtonElement>) => void }).onClick?.(
      e,
    );
  };

  return (
    <TButton
      variant={variant}
      radius={radius}
      size={getTButtonSize()}
      className={clsx(
        // Layout: justify-between pushes the icon to the far edge and
        // the text to the opposite edge so they stay visually apart
        // regardless of button width. gap-4 sets the minimum spacing
        // when the button is sized to fit-content (so icon + text don't
        // collide on a tight button).
        // [&_.mantine-Button-label]:overflow-visible — Mantine's inner
        // label container clips overflow by default, which clipped the
        // arrow's group-hover:translate-x-1 animation. Letting it overflow
        // is fine because the outer button has its own padding.
        // No hover:shadow / hover:scale — restrained look per playground.
        "group flex h-auto w-fit items-center justify-between gap-4 font-bricolage-grotesque font-medium transition-all duration-300 [&_.mantine-Button-label]:overflow-visible",
        getSizeClasses(),
        className,
      )}
      {...props}
      onClick={handleClick}
    >
      {iconPosition === "left" && renderIcon()}
      <span className="whitespace-nowrap">{children}</span>
      {iconPosition === "right" && renderIcon()}
    </TButton>
  );
};

export default CTAButton;
